'use client';
/**
 * The export flow, in one Dialog. Used from the editor top bar and from the albums list, so it owns
 * every bit of its own state — including finding out what the album already has.
 *
 * The shape of it:
 *
 *   main (no export yet)  one sentence describing the file you'll get + one button.
 *   main (has an export)  the latest PDF card — when it was made, what it is, download / share
 *                         link / "the album changed since" — with "generate a new one" in the
 *                         action bar under it.
 *   progress              the live job (poll, stage, bar, cancel).
 *   done                  the very same three actions as the card, so finishing an export and
 *                         re-opening the dialog tomorrow land you in the same place.
 *
 * Everything that used to greet people on open — DPI, JPEG quality, page mode, cover, print-ready,
 * flipbook — now lives behind one collapsed disclosure (`<AdvancedOptions>`), and whatever is in
 * effect is always spelled out in the sentence above it.
 *
 * The main view also carries the print-readiness list (docs/10-ux-audit.md · UX-32): every likely
 * output problem, the page it is on, and a way to jump straight to it. It is drawn only when the
 * editor store is actually holding THIS album — opened from the albums list there is no document
 * in memory to check, and a half-answer there would be worse than no answer.
 */
import { AlbumLoader } from '@/components/ui/album-loader';
import * as React from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { ArrowUpRight, CircleCheck, Download, HardDriveDownload, Link2, OctagonAlert, RefreshCw, RotateCcw, TriangleAlert } from 'lucide-react';
import type { ExportOptions } from '@freealbum/document';
import { Badge, Button, Dialog, Spinner, toast } from '@freealbum/ui';
import { api, createExport, pollExport } from '@/editor/api';
import { useEditor } from '@/editor/store';
import type { Target } from '@/editor/types';
import { printService } from '@freealbum/document';
import { checkReadiness, measureTextIssues, sortIssues, type ReadinessIssue, type ReadinessSeverity } from '@/editor/readiness';
import { backupDownloadUrl, ensureFlipbookLink } from '@/lib/api';
import type { ExportJobDto } from '@freealbum/shared';
import { useApiErrorMessage } from '@/hooks/use-api-error';
import { useFileDownload, type FileDownload } from '@/hooks/use-file-download';
import { classifyFailure } from './failure';
import { Link } from '@/i18n/navigation';
import { cn, copyToClipboard, formatBytes, formatDate } from '@/lib/utils';
import { AdvancedOptions, estimateExportBytes, loadOptions, storeOptions, useOptionsSummary } from './export-options';
import { ExportProgress } from './export-progress';

export { estimateExportBytes } from './export-options';

export interface ExportDialogProps {
  albumId: string;
  albumTitle: string;
  /** number of inner pages — feeds the size estimate */
  pageCount: number;
  /** trim size of one page; without it the estimate assumes sq20 */
  page?: { widthMm: number; heightMm: number };
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Run before a job is created, and resolve true only once the server holds the current document.
   * The editor passes its save barrier: an export that starts while a PATCH is still in flight
   * prints the previous version of the album (docs/10-ux-audit.md · UX-03).
   */
  beforeStart?: () => Promise<boolean>;
}

type View = 'main' | 'progress' | 'done' | 'failed';

/**
 * Creating an export touches the album (`touchAlbum`), and finishing one sets `album.updatedAt` to
 * the job's `finishedAt` exactly — so "the album changed" means updatedAt moved past the newest
 * thing any job did. A couple of seconds of slack absorbs the write ordering inside those routes.
 */
const CHANGE_GRACE_MS = 2_000;

const ts = (iso: string | null | undefined): number => {
  const n = iso ? Date.parse(iso) : Number.NaN;
  return Number.isFinite(n) ? n : 0;
};

export function ExportDialog({ albumId, albumTitle, pageCount, page, open, onOpenChange, beforeStart }: ExportDialogProps): React.JSX.Element | null {
  const t = useTranslations('export');
  const tBackup = useTranslations('backup');
  const tCommon = useTranslations('common');
  const tEditor = useTranslations('editor');
  const locale = useLocale();
  const errorMessage = useApiErrorMessage();
  const summarize = useOptionsSummary();

  const [initial] = React.useState(() => loadOptions(albumId));
  const [options, setOptions] = React.useState<ExportOptions>(initial.options);
  /** False until this device has an opinion, which lets the last finished job supply one instead. */
  const rememberedRef = React.useRef(initial.remembered);

  const [view, setView] = React.useState<View>('main');
  const [advanced, setAdvanced] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [latest, setLatest] = React.useState<ExportJobDto | null>(null);
  const [stale, setStale] = React.useState(false);
  const [job, setJob] = React.useState<ExportJobDto | null>(null);
  const [starting, setStarting] = React.useState(false);
  const [backingUp, setBackingUp] = React.useState(false);
  const [cancelling, setCancelling] = React.useState(false);
  const [copying, setCopying] = React.useState(false);
  const download = useFileDownload({ onError: () => toast({ title: t('downloadFailed'), variant: 'error' }) });
  const [pollError, setPollError] = React.useState<string | null>(null);

  const patch = React.useCallback(
    (next: Partial<ExportOptions>) => {
      setOptions((prev) => {
        const merged = { ...prev, ...next };
        storeOptions(albumId, merged);
        return merged;
      });
      rememberedRef.current = true;
    },
    [albumId],
  );

  const estimate = React.useMemo(() => estimateExportBytes(pageCount, options, page), [pageCount, options, page]);

  // An album that knows where it will be printed opens the dialog on that shop's profile, unless
  // this device already has an opinion about this album's export.
  const albumService = useEditor((s) => (s.ready && s.albumId === albumId ? s.doc.print.service : 'none'));
  const albumBinding = useEditor((s) => (s.ready && s.albumId === albumId ? s.doc.print.binding : 'hardcover'));
  React.useEffect(() => {
    if (!open || rememberedRef.current || albumService === 'none') return;
    const spec = printService(albumService)?.covers[albumBinding];
    setOptions((prev) =>
      prev.printService === albumService ? prev : { ...prev, printService: albumService, ...(spec && prev.cover !== 'none' ? { cover: spec.modes[0] } : {}) },
    );
  }, [open, albumService, albumBinding]);
  const current = summarize(options);

  // ── print readiness ─────────────────────────────────────────────────────────
  // The editor store is the only place the live document lives. From the albums list it holds
  // someone else's album — or nothing at all — so everything below is gated on it holding ours.
  const editorReady = useEditor((s) => s.ready);
  const editorAlbumId = useEditor((s) => s.albumId);
  const doc = useEditor((s) => s.doc);
  const photoMap = useEditor((s) => s.photos);
  const setTarget = useEditor((s) => s.setTarget);
  const select = useEditor((s) => s.select);
  const hasDocument = editorReady && editorAlbumId === albumId;

  /** Warnings the person has said are on purpose. Blank pages are a design, not a mistake. */
  const [acknowledged, setAcknowledged] = React.useState<string[]>([]);
  /** Text overflow can only be known by measuring; until that lands the list simply omits it. */
  const [textIssues, setTextIssues] = React.useState<ReadinessIssue[]>([]);

  React.useEffect(() => {
    if (!open || !hasDocument) return;
    let alive = true;
    void measureTextIssues(doc).then(
      (found) => {
        if (alive) setTextIssues(found);
      },
      // A measurement that could not be taken is not a finding.
      () => {
        if (alive) setTextIssues([]);
      },
    );
    return () => {
      alive = false;
    };
  }, [open, hasDocument, doc]);

  const issues = React.useMemo(
    () => (hasDocument ? sortIssues([...checkReadiness(doc, photoMap, options), ...textIssues]) : []),
    [hasDocument, doc, photoMap, options, textIssues],
  );
  const openIssues = issues.filter((issue) => !acknowledged.includes(issue.key));
  const blockers = openIssues.filter((issue) => issue.severity === 'blocker');
  const warnings = openIssues.filter((issue) => issue.severity === 'warning');

  // `errorMessage` closes over the translator and is not referentially stable; the load effect must
  // run on open, not on every render, so it reads the latest one through a ref.
  const errRef = React.useRef(errorMessage);
  errRef.current = errorMessage;

  // ── what does this album already have? ───────────────────────────────────────
  React.useEffect(() => {
    if (!open) return;
    let alive = true;
    setLoading(true);
    setLoadError(null);
    setView('main');
    setJob(null);
    setPollError(null);

    void (async () => {
      let jobs: ExportJobDto[];
      try {
        ({ jobs } = await api.exports.listForAlbum(albumId));
      } catch (err) {
        if (alive) {
          setLoadError(errRef.current(err));
          setLoading(false);
        }
        return;
      }
      if (!alive) return;

      // The route returns newest first.
      const done = jobs.find((j) => j.status === 'done') ?? null;
      const active = jobs.find((j) => j.status === 'queued' || j.status === 'rendering') ?? null;
      setLatest(done);
      // Nothing remembered on this device, but the album itself remembers: reuse what it was
      // exported with last, so a new phone still opens on the right settings.
      if (!rememberedRef.current && done) setOptions(done.options);
      // Re-opening mid-render drops you straight back on the progress bar.
      if (active) {
        setJob(active);
        setView('progress');
      }
      setLoading(false);
      if (!done) {
        setStale(false);
        return;
      }

      try {
        const touched = Math.max(...jobs.map((j) => ts(j.finishedAt) || ts(j.startedAt) || ts(j.createdAt)));
        const { album } = await api.albums.get(albumId);
        if (alive) setStale(ts(album.updatedAt) > touched + CHANGE_GRACE_MS);
      } catch {
        // The note is a courtesy; not being able to check just means we don't nag.
      }
    })();

    return () => {
      alive = false;
    };
  }, [open, albumId]);

  // ── polling ─────────────────────────────────────────────────────────────────
  // Keyed on the job id alone — the handlers close over `t`/`toast`, which are not guaranteed to be
  // referentially stable, so they go through a ref instead of the deps.
  const handlersRef = React.useRef<{ onUpdate: (j: ExportJobDto) => void; onError: (e: unknown) => void }>({
    onUpdate: () => {},
    onError: () => {},
  });
  handlersRef.current.onUpdate = (next: ExportJobDto) => {
    setJob(next);
    setPollError(null);
    if (next.status === 'done') {
      setLatest(next);
      setStale(false);
      setView('done');
    } else if (next.status === 'failed') setView('failed');
    else if (next.status === 'cancelled') {
      setView('main');
      toast({ title: t('cancelled'), variant: 'info' });
    }
  };
  handlersRef.current.onError = (err: unknown) => setPollError(errRef.current(err));

  const pollingJobId = open && view === 'progress' ? (job?.id ?? null) : null;
  React.useEffect(() => {
    if (!pollingJobId) return;
    // pollExport returns its own stop function — that is our cleanup on unmount / close / settle.
    return pollExport(
      pollingJobId,
      (next) => handlersRef.current.onUpdate(next),
      (err) => handlersRef.current.onError(err),
    );
  }, [pollingJobId]);

  // ── actions ─────────────────────────────────────────────────────────────────
  const start = async () => {
    setStarting(true);
    setPollError(null);
    try {
      if (beforeStart && !(await beforeStart())) {
        // Nothing is exported from a document the server has not accepted. The editor's own save
        // indicator and conflict dialog carry the recovery; this only refuses to print the wrong one.
        toast({ title: t('needsSave'), description: t('needsSaveBody'), variant: 'error' });
        return;
      }
      storeOptions(albumId, options);
      rememberedRef.current = true;
      const state = useEditor.getState();
      const created = await createExport(albumId, options, beforeStart && state.albumId === albumId ? state.version : undefined);
      setJob(created);
      setStale(false);
      if (created.status === 'done') setLatest(created);
      setView(created.status === 'done' ? 'done' : created.status === 'failed' ? 'failed' : 'progress');
    } catch (err) {
      toast({ title: t('startFailed'), description: errorMessage(err), variant: 'error' });
    } finally {
      setStarting(false);
    }
  };

  const downloadBackup = async () => {
    if (backingUp) return;
    setBackingUp(true);
    try {
      if (beforeStart && !(await beforeStart())) {
        toast({ title: t('needsSave'), description: t('backupNeedsSave'), variant: 'error' });
        return;
      }
      const state = useEditor.getState();
      const suffix = beforeStart && state.albumId === albumId ? `?version=${state.version}` : '';
      const response = await fetch(backupDownloadUrl(albumId) + suffix);
      if (!response.ok) throw new Error('backup');
      const url = URL.createObjectURL(await response.blob());
      const link = document.createElement('a');
      link.href = url;
      link.download = `${albumTitle || 'album'}.fab`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch { toast({ title: t('backupFailed'), variant: 'error' }); }
    finally { setBackingUp(false); }
  };

  const cancel = async () => {
    if (!job) return;
    setCancelling(true);
    try {
      await api.exports.cancel(job.id);
      setView('main');
      toast({ title: t('cancelled'), variant: 'info' });
    } catch (err) {
      toast({ title: errorMessage(err), variant: 'error' });
    } finally {
      setCancelling(false);
    }
  };

  /** The page-flip link for a finished export: reuse the one that already points at it, or mint it. */
  const shareLink = async (finished: ExportJobDto) => {
    setCopying(true);
    try {
      const link = await ensureFlipbookLink(albumId, finished.finishedAt);
      const ok = await copyToClipboard(link.url);
      toast({ title: ok ? t('viewerCopied') : link.url, variant: ok ? 'success' : 'info' });
    } catch (err) {
      toast({ title: errorMessage(err), variant: 'error' });
    } finally {
      setCopying(false);
    }
  };

  const retry = () => {
    setJob(null);
    setPollError(null);
    setView('main');
  };

  /** Get out of the way, move the editor to the surface, and put the offender under the cursor. */
  const goToIssue = (issue: ReadinessIssue) => {
    onOpenChange(false);
    // `setTarget` clears the selection, so the selection has to come second.
    setTarget(issue.target);
    if (issue.elementId) select([issue.elementId], { target: issue.target });
  };

  if (!open) return null;

  const title = view === 'progress' ? t('progressTitle') : view === 'done' ? t('doneTitle') : view === 'failed' ? t('failedTitle') : t('title');
  const description = view === 'main' ? t('lead') : albumTitle || undefined;

  return (
    <Dialog open={open} onOpenChange={onOpenChange} title={title} description={description} size="md" footer={renderFooter()}>
      {view === 'main' ? renderMain() : null}
      {view === 'progress' && job ? (
        <div className="flex flex-col gap-3">
          <ExportProgress job={job} />
          {pollError ? <p className="rounded-md bg-danger-soft px-3 py-2 text-sm text-danger">{pollError}</p> : null}
        </div>
      ) : null}
      {view === 'done' && job ? renderDone(job) : null}
      {view === 'failed' && job ? renderFailed(job) : null}
    </Dialog>
  );

  // ── views ───────────────────────────────────────────────────────────────────

  function renderMain(): React.JSX.Element {
    if (loading) {
      return (
        <div className="grid min-h-32 place-items-center">
          <AlbumLoader label={t('loading')} />
        </div>
      );
    }
    return (
      <div className="flex flex-col gap-4">
        {loadError ? <p className="rounded-md bg-danger-soft px-3 py-2 text-sm text-danger">{loadError}</p> : null}
        {latest ? renderLatestCard(latest) : null}
        {renderReadiness()}
        {renderNext()}
      </div>
    );
  }

  /** Where an issue is, in the words the editor uses for its own surfaces. */
  function placeOf(target: Target): string {
    return target === 'cover' ? tEditor('cover') : tEditor('spread', { index: target + 1 });
  }

  /**
   * Everything we can honestly say will be different on paper, before anyone waits for a render.
   * Blockers are things the file will simply be missing; warnings are things that are often on
   * purpose, so each one can be waved off and none of them stands in the way of exporting.
   */
  function renderReadiness(): React.JSX.Element | null {
    if (!hasDocument) return null;
    if (openIssues.length === 0) {
      return (
        <p className="flex items-center gap-2 rounded-md bg-success-soft px-3 py-2 text-xs text-success">
          <CircleCheck aria-hidden className="size-4 shrink-0" />
          {t('readiness.clear')}
        </p>
      );
    }
    return (
      <section className="flex flex-col gap-3 rounded-lg border border-line bg-paper-2 p-3">
        <div>
          <h3 className="text-sm font-medium text-ink">{t('readiness.title')}</h3>
          <p className="text-xs text-ink-3">{t('readiness.lead')}</p>
        </div>
        {blockers.length > 0 ? renderIssueGroup('blocker', blockers) : null}
        {warnings.length > 0 ? renderIssueGroup('warning', warnings) : null}
      </section>
    );
  }

  function renderIssueGroup(severity: ReadinessSeverity, list: ReadinessIssue[]): React.JSX.Element {
    const blocking = severity === 'blocker';
    const Icon = blocking ? OctagonAlert : TriangleAlert;
    const tone = blocking ? 'text-danger' : 'text-warning';
    return (
      <div className="flex flex-col gap-1.5">
        <h4 className={`text-xs font-medium ${tone}`}>
          {blocking ? t('readiness.blockers', { count: list.length }) : t('readiness.warnings', { count: list.length })}
        </h4>
        <ul className="flex flex-col gap-1.5">
          {list.map((issue) => (
            <li key={issue.key} className="flex flex-wrap items-start gap-x-2 gap-y-1 rounded-md bg-surface px-2.5 py-2">
              <Icon aria-hidden className={`mt-0.5 size-4 shrink-0 ${tone}`} />
              <div className="min-w-0 flex-1">
                <p className="text-xs text-ink">
                  {t(`readiness.issue.${issue.kind}` as 'readiness.issue.emptySlot', { dpi: issue.dpi ?? 0 })}
                </p>
                <p className="text-2xs text-ink-3">{t('readiness.where', { place: placeOf(issue.target) })}</p>
              </div>
              <div className="flex shrink-0 items-center gap-0.5">
                <Button size="sm" variant="ghost" onClick={() => goToIssue(issue)} endIcon={<ArrowUpRight aria-hidden />}>
                  {t('readiness.goTo')}
                </Button>
                {blocking ? null : (
                  <Button size="sm" variant="ghost" onClick={() => setAcknowledged((prev) => [...prev, issue.key])}>
                    {t('readiness.dismiss')}
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  /** What the album already has: made-at, what it is, and the three things you can do with it. */
  function renderLatestCard(done: ExportJobDto): React.JSX.Element {
    return (
      <section className="flex flex-col gap-3 rounded-lg border border-line bg-paper-2 p-3">
        <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
          <h3 className="text-sm font-medium text-ink">{t('latestTitle')}</h3>
          <span className="text-xs text-ink-3">
            {t('latestMade', { date: formatDate(done.finishedAt ?? done.createdAt, locale, { dateStyle: 'medium', timeStyle: 'short' }) })}
          </span>
        </div>
        <p className="text-xs text-ink-2">{summarize(done.options).sentence}</p>
        {stale ? (
          <p className="flex items-start gap-2 rounded-md bg-warning-soft px-2.5 py-2 text-xs text-warning">
            <TriangleAlert aria-hidden className="mt-px size-4 shrink-0" />
            <span>{t('latestChanged')}</span>
          </p>
        ) : null}
        {renderResultActions(done)}
      </section>
    );
  }

  /** Download the album (and the cover), or copy the link people can flip through. */
  function renderResultActions(done: ExportJobDto): React.JSX.Element {
    return (
      <div className="flex flex-col gap-2">
        {done.files.album ? (
          <DownloadLink id="album" href={done.files.album} download={download} className={DOWNLOAD_LINK_CLASS} label={t('downloadAlbum')} pendingLabel={t('downloadPreparing')} />
        ) : null}
        {done.files.cover ? (
          <DownloadLink id="cover" href={done.files.cover} download={download} className={SECONDARY_LINK_CLASS} label={t('downloadCover')} pendingLabel={t('downloadPreparing')} />
        ) : null}
        {done.flipbookReady ? (
          <Button variant="secondary" onClick={() => void shareLink(done)} loading={copying} block startIcon={<Link2 aria-hidden />}>
            {t('shareLink')}
          </Button>
        ) : (
          <p className="text-xs text-ink-3">{t('shareUnavailable')}</p>
        )}
      </div>
    );
  }

  /**
   * The one decision: this is the file you will get. Everything that could change it is one
   * disclosure away, and whatever is currently in effect is already in the sentence.
   */
  function renderNext(): React.JSX.Element {
    return (
      <div className="flex flex-col gap-3">
        {latest ? <h3 className="text-sm font-medium text-ink">{t('nextTitle')}</h3> : null}
        <div>
          <p className="text-sm text-ink">{current.sentence}</p>
          {current.tags.length > 0 ? (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {current.tags.map((tag) => (
                <Badge key={tag} variant="neutral">
                  {tag}
                </Badge>
              ))}
            </div>
          ) : null}
          <p className="mt-2 text-xs text-ink-3">{t('estimate', { size: formatBytes(estimate, locale) })}</p>
          <p className="text-xs text-ink-3">{t('estimateHint')}</p>
        </div>

        <AdvancedOptions options={options} onChange={patch} open={advanced} onOpenChange={setAdvanced} page={page ?? doc.page} pageCount={pageCount} rtl={hasDocument && doc.cover.binding === 'rtl'} doc={hasDocument ? doc : null}>
          {/*
            The other file this album can produce. It belongs somewhere in here because "export" is
            where people come looking for a copy of their work — but the two are not interchangeable,
            so the copy leads with the difference: the PDF is for printing, the .fab is for getting
            the album back. It is not the main event, so it sits under the disclosure.
          */}
          <section className="flex flex-col gap-2 border-t border-line pt-4">
            <h4 className="flex items-center gap-2 text-sm font-medium text-ink">
              <HardDriveDownload aria-hidden className="size-4 text-ink-3" />
              {tBackup('download.title')}
            </h4>
            <p className="text-xs text-ink-2">{tBackup('download.body')}</p>
            <button type="button" disabled={backingUp} aria-busy={backingUp || undefined} onClick={() => void downloadBackup()} className={SECONDARY_LINK_CLASS}>
              {backingUp ? <Spinner /> : <HardDriveDownload aria-hidden className="size-4" />}
              {backingUp ? tBackup('download.preparing') : tBackup('download.cta')}
            </button>
          </section>
        </AdvancedOptions>

        {latest ? (
          <Link
            href={`/albums/${albumId}/exports`}
            className="self-start rounded-md text-xs text-ink-3 underline underline-offset-2 hover:text-ink-2 focus-visible:outline-2 focus-visible:outline-focus focus-visible:outline-offset-2"
          >
            {t('historyLink')}
          </Link>
        ) : null}
      </div>
    );
  }

  /** Identical to the latest card's actions — finishing an export leaves you exactly where
   * re-opening the dialog would. */
  function renderDone(finished: ExportJobDto): React.JSX.Element {
    return (
      <div className="flex flex-col gap-4">
        <p className="text-sm text-ink-2">{summarize(finished.options).sentence}</p>
        {renderResultActions(finished)}
        {renderNext()}
      </div>
    );
  }

  function renderFailed(failed: ExportJobDto): React.JSX.Element {
    const kind = classifyFailure(failed.error);
    return (
      <div className="flex flex-col gap-3">
        <p className="text-sm text-ink">{t(`failure.${kind}.what` as 'failure.unknown.what')}</p>
        <p className="text-sm text-ink-2">{t(`failure.${kind}.next` as 'failure.unknown.next')}</p>
        {failed.error ? (
          // The raw message is diagnostic, not instruction: it stays available, and stays closed.
          <details className="rounded-md bg-paper-2 px-3 py-2">
            <summary className="cursor-pointer text-xs text-ink-3">{t('failure.details')}</summary>
            <pre dir="ltr" className="mt-2 max-h-40 overflow-auto text-start font-mono text-2xs whitespace-pre-wrap text-ink-3">
              {failed.error}
            </pre>
          </details>
        ) : null}
      </div>
    );
  }

  function renderFooter(): React.JSX.Element {
    if (view === 'main') {
      return latest ? (
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {tCommon('close')}
          </Button>
          <Button variant="primary" onClick={() => void start()} loading={starting} disabled={loading} startIcon={<RefreshCw aria-hidden />}>
            {starting ? t('starting') : t('regenerate')}
          </Button>
        </>
      ) : (
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {tCommon('cancel')}
          </Button>
          <Button variant="primary" onClick={() => void start()} loading={starting} disabled={loading}>
            {starting ? t('starting') : t('start')}
          </Button>
        </>
      );
    }
    if (view === 'progress') {
      return (
        <Button variant="ghost" onClick={() => void cancel()} loading={cancelling}>
          {t('cancel')}
        </Button>
      );
    }
    if (view === 'failed') {
      return (
        <>
          <Button variant="ghost" onClick={retry}>
            {t('failure.changeSettings')}
          </Button>
          {/* Retry means "the same album, the same settings, again" — not "start the form over". */}
          <Button variant="primary" onClick={() => void start()} loading={starting} startIcon={<RotateCcw aria-hidden />}>
            {t('failure.retrySame')}
          </Button>
        </>
      );
    }
    return (
      <>
        <Button variant="ghost" onClick={() => onOpenChange(false)}>
          {tCommon('close')}
        </Button>
        <Button variant="primary" onClick={() => void start()} loading={starting} startIcon={<RefreshCw aria-hidden />}>
          {starting ? t('starting') : t('regenerate')}
        </Button>
      </>
    );
  }
}

/**
 * A download that says so. Still a real link with the file's address — the browser, and anyone
 * copying it, get the same URL as before — but the press goes through `useFileDownload`, which
 * spins this one control until the file starts arriving and holds the others meanwhile (UX-47).
 */
function DownloadLink({
  id,
  href,
  download,
  className,
  label,
  pendingLabel,
}: {
  id: string;
  href: string;
  download: FileDownload;
  className: string;
  label: string;
  pendingLabel: string;
}) {
  const mine = download.pending === id;
  const held = download.pending !== null && !mine;
  return (
    <a
      href={href}
      download
      aria-busy={mine || undefined}
      aria-disabled={held || undefined}
      className={cn(className, download.pending !== null && 'pointer-events-none', held && 'opacity-45')}
      onClick={(event) => {
        event.preventDefault();
        download.start(id, href);
      }}
    >
      {mine ? <Spinner /> : <Download aria-hidden className="size-4" />}
      {mine ? pendingLabel : label}
    </a>
  );
}

const LINK_BUTTON_BASE =
  'inline-flex h-touch w-full items-center justify-center gap-2 rounded-md px-4 text-sm font-medium shadow-xs ' +
  'transition-colors duration-(--duration-fast) focus-visible:outline-2 focus-visible:outline-focus focus-visible:outline-offset-1';
const DOWNLOAD_LINK_CLASS = `${LINK_BUTTON_BASE} bg-accent text-accent-fg hover:bg-accent-hover`;
const SECONDARY_LINK_CLASS = `${LINK_BUTTON_BASE} border border-line bg-surface text-ink hover:bg-paper-2 hover:border-line-strong`;
